import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, Container, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageHeader from '~/components/Common/Elements/PageHeader';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import {
  useDeleteFacebookContentTemplate,
  useGetFacebookContentTemplates,
} from '~/hooks/apis/public-offer-feed/useFacebookContentTemplates';

import { FacebookContentTemplate } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

import CreateTemplateModal from './CreateTemplateModal';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

const getColumns = ({ onDelete }: { onDelete: (row: FacebookContentTemplate) => void }): Array<GridColDef> => [
  {
    field: 'id',
    headerName: 'ID',
    ...sharedOpts,
    flex: 0.5,
  },
  {
    field: 'name',
    headerName: 'Name',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.created_at)}</Typography>,
  },
  {
    field: 'updated_at',
    headerName: 'Updated At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.updated_at)}</Typography>,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    flex: 0.8,
    renderCell: (params) => (
      <Button variant="outlined" size="small" color="error" onClick={() => onDelete(params.row)}>
        Delete
      </Button>
    ),
  },
];

export default function FacebookImageTemplatesContainer() {
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();
  const [open, setOpen] = useState(false);
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const { data, isLoading, error } = useGetFacebookContentTemplates();
  const deleteMutation = useDeleteFacebookContentTemplate();

  // Handle different possible response structures
  const templates = useMemo(() => {
    if (!data) return [];

    if (Array.isArray(data.result)) {
      return data.result;
    }

    if (Array.isArray(data)) {
      return data;
    }

    return [];
  }, [data]);

  const rows = useMemo(() => templates.map((template) => ({ id: template.id, ...template })), [templates]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = async (template: FacebookContentTemplate) => {
    const confirmed = await showConfirmDialog({
      title: 'Delete Facebook Content Template?',
      description: `Are you sure you want to delete template "${template.name}"? This action cannot be undone.`,
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(template.id);
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to delete template: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  };

  return (
    <Container maxWidth="xl">
      <PageHeader title="Facebook Image Templates">
        <Box>
          <Button variant="contained" color="primary" onClick={handleOpen}>
            Create Template
          </Button>
        </Box>
      </PageHeader>

      <Box mt={4}>
        {error && (
          <Box mb={2}>
            <Typography color="error">
              Error loading templates: {(error as Error).message || 'Unknown error'}
            </Typography>
          </Box>
        )}
        {!isLoading && templates.length === 0 && !error && (
          <Box mb={2}>
            <Typography color="text.secondary">No templates found. Create your first template above.</Typography>
          </Box>
        )}
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize: newPageSize }) => handlePageChange(page, newPageSize)}
          loading={isLoading}
          rows={rows}
          columns={getColumns({ onDelete: handleDelete })}
          autoHeight
          rowCount={templates.length}
          paginationMode="client"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>

      <CreateTemplateModal open={open} onClose={handleClose} />
    </Container>
  );
}
